import {WordWithId} from "../../../../../common/types/words";
import {VocabularyActionDispatch} from "../../../pages/Vocabulary";
import {useState} from "react";
import UseTwoStepUpdate from "../../../hooks/useTwoStepUpdate";


export interface useWordListReturn<T extends WordWithId> {
    currentWordList: T[]
    selectedWord: T | null
    setSelectedWord: (word: T | null) => void
    actionDispatch: VocabularyActionDispatch<T>
}

function useWordList<T extends WordWithId>(initialWordList: T[], actionDispatch?: VocabularyActionDispatch<T>): useWordListReturn<T> {

    const [currentWordList, setCurrentWordList] = useState<T[]>(initialWordList);
    const [selectedWord, setSelectedWord] = useState<T | null>(null);

    const onWordAdded = (addPromise: Promise<T>)=> {
        actionDispatch?.add?.(addPromise);
        addPromise.then((word) => {
           setCurrentWordList(prevList => ([...prevList, word]));
        });
    };

    const {handleTwoStepUpdate} = UseTwoStepUpdate(
        () => selectedWord,
        (updatedWord) =>
            setCurrentWordList(prevList => prevList.map(word => word.wordId === updatedWord?.wordId ? {...word, ...updatedWord} : word))
    );

    const onWordUpdated = (updatedWord: T, updateWordPromise: Promise<T>) => {
        actionDispatch?.update?.(updatedWord, updateWordPromise);
        setSelectedWord(prev => updatedWord.wordId === prev?.wordId ? updatedWord : prev);
        handleTwoStepUpdate(updatedWord, updateWordPromise);
    };

    const onWordDeleted = (deleteIdPromise: Promise<string>) => {
        actionDispatch?.delete?.(deleteIdPromise);
        deleteIdPromise.then((deletedId) => {
            setSelectedWord(prev => prev?.wordId === deletedId ? null : selectedWord);
            setCurrentWordList(prevList => prevList.filter(word => word.wordId !== deletedId));
        })
    };

    return {
        currentWordList,
        selectedWord,
        setSelectedWord,
        actionDispatch: {
            add: onWordAdded,
            update: onWordUpdated,
            delete: onWordDeleted
        }
    }

}

export default useWordList;